import type { NostrEvent } from '@nostrify/nostrify';

/** A named group of relays that forms one explorable feed. */
export interface RelaySet {
  id: string;
  name: string;
  relayUrls: string[];
}

export type FeedType = 'following' | 'relays' | 'relay' | 'pinned';

export interface FeedInfo {
  feedType: FeedType;
  /** RelaySet id when feedType === 'relays', relay url when 'relay'. */
  id?: string;
  name?: string;
}

export const DEFAULT_RELAY_SETS: RelaySet[] = [
  {
    id: 'global',
    name: 'Global',
    relayUrls: [
      'wss://relay.damus.io/',
      'wss://relay.primal.net/',
      'wss://nos.lol/',
      'wss://relay.nostr.band/',
    ],
  },
  {
    id: 'bitcoin',
    name: 'Bitcoin',
    relayUrls: [
      'wss://relay.primal.net/',
      'wss://nos.lol/',
      'wss://nostr-pub.wellorder.net/',
      'wss://relay.damus.io/',
    ],
  },
  {
    id: 'tech',
    name: 'Tech & Dev',
    relayUrls: [
      'wss://relay.damus.io/',
      'wss://relay.nostr.band/',
      'wss://nos.lol/',
      'wss://nostr.mom/',
    ],
  },
  {
    id: 'art',
    name: 'Art & Culture',
    relayUrls: [
      'wss://relay.primal.net/',
      'wss://relay.damus.io/',
      'wss://nostr.mom/',
      'wss://purplepag.es/',
    ],
  },
];

export const POPULAR_RELAYS: { url: string; name: string }[] = [
  { url: 'wss://relay.damus.io/', name: 'Damus' },
  { url: 'wss://relay.primal.net/', name: 'Primal' },
  { url: 'wss://nos.lol/', name: 'nos.lol' },
  { url: 'wss://relay.nostr.band/', name: 'nostr.band' },
  { url: 'wss://nostr.mom/', name: 'nostr.mom' },
  { url: 'wss://purplepag.es/', name: 'Purple Pages' },
  { url: 'wss://relay.snort.social/', name: 'Snort' },
  { url: 'wss://nostr-pub.wellorder.net/', name: 'Wellorder' },
  { url: 'wss://relay.ditto.pub/', name: 'Ditto' },
  { url: 'wss://relay.dreamith.to/', name: 'Dreamith' },
  { url: 'wss://offchain.pub/', name: 'Offchain' },
  { url: 'wss://nostr.wine/', name: 'Nostr Wine' },
];

const WSS_RE = /^wss?:\/\/.+/i;

/** Normalize a relay URL: lowercase host, ensure trailing slash. */
export function normalizeRelayUrl(url: string): string {
  let u = url.trim();
  if (!/^wss?:\/\//i.test(u)) u = `wss://${u}`;
  try {
    const parsed = new URL(u);
    if (parsed.protocol !== 'wss:' && parsed.protocol !== 'ws:') return u;
    const host = parsed.hostname.toLowerCase();
    const port = parsed.port ? `:${parsed.port}` : '';
    const path = parsed.pathname === '/' ? '/' : parsed.pathname;
    return `${parsed.protocol}//${host}${port}${path}`;
  } catch {
    return u.endsWith('/') ? u : `${u}/`;
  }
}

export function isWebsocketUrl(url: string): boolean {
  return WSS_RE.test(url.trim());
}

/** Shorten a pubkey/npub for display. */
export function shortId(id: string, len = 8): string {
  if (id.length <= len + 4) return id;
  return `${id.slice(0, len)}…${id.slice(-4)}`;
}

export function getTags(event: NostrEvent, name: string): string[][] {
  return event.tags.filter((t) => t[0] === name);
}

export function getTagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find((t) => t[0] === name)?.[1];
}

/** NIP-10 root id (first `e` tag with marker root, else first e tag). */
export function rootEventId(event: NostrEvent): string | undefined {
  const eTags = getTags(event, 'e');
  if (eTags.length === 0) return undefined;
  return eTags.find((t) => t[3] === 'root')?.[1] ?? eTags[0][1];
}

/** NIP-10 reply id (e tag with marker reply, else last e tag if >1). */
export function replyEventId(event: NostrEvent): string | undefined {
  const eTags = getTags(event, 'e');
  if (eTags.length === 0) return undefined;
  const marked = eTags.find((t) => t[3] === 'reply')?.[1];
  if (marked) return marked;
  return eTags.length > 1 ? eTags[eTags.length - 1][1] : undefined;
}

export function isRepost(event: NostrEvent): boolean {
  return event.kind === 6;
}

export function repostedId(event: NostrEvent): string | undefined {
  return getTagValue(event, 'e');
}

export function timeAgo(tsSeconds: number, nowMs = Date.now()): string {
  const diff = Math.max(1, Math.floor(nowMs / 1000) - tsSeconds);
  if (diff < 60) return `${diff}s`;
  const m = Math.floor(diff / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.floor(d / 7)}w`;
  if (d < 365) return `${Math.floor(d / 30)}mo`;
  return `${Math.floor(d / 365)}y`;
}

export function fullDate(tsSeconds: number): string {
  return new Date(tsSeconds * 1000).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Extract lowercase hashtags (#tag) from content. */
export function extractHashtags(content: string): string[] {
  const out = new Set<string>();
  const re = /(?:^|\s)#([\p{L}\p{N}_]+)/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) out.add(m[1].toLowerCase());
  return [...out];
}

/** Dedupe events by id, newest first. */
export function dedupeEvents(events: NostrEvent[]): NostrEvent[] {
  const seen = new Set<string>();
  return events
    .filter((e) => {
      if (!e.id || seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    })
    .sort((a, b) => b.created_at - a.created_at);
}

/** Relay websocket URL -> http(s) origin for NIP-11 fetch. */
export function relayHttpOrigin(relayUrl: string): string | null {
  try {
    const u = new URL(relayUrl);
    const scheme = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${scheme}//${u.host}`;
  } catch {
    return null;
  }
}

/** Storage keys (localStorage) for Jumble-clone state. */
export const StorageKey = {
  RELAY_SETS: 'jumble:relay-sets',
  ACTIVE_FEED: 'jumble:active-feed',
  BOOKMARKS: 'jumble:bookmarks',
  MUTED_PUBKEYS: 'jumble:muted-pubkeys',
  MUTED_WORDS: 'jumble:muted-words',
  PINNED_PUBKEYS: 'jumble:pinned-pubkeys',
  SHOW_REPLIES: 'jumble:show-replies',
} as const;

export function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or unavailable — ignore
  }
}
